import { queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import RelatedContentSampleQuery, {
	type useRelatedContentQuery$data,
	type useRelatedContentQuery$variables,
} from '../hooks/__generated__/useRelatedContentQuery.graphql.ts';
import {
	buildRelatedContentSampleVariables,
	normalizeRelatedContentSampleData,
	type RelatedContentSampleResult,
	type RelatedContentSampleUnavailableReason,
} from './relatedContentSsrState.ts';

type RelatedContentSampleCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';

const unavailable = (
	reason: RelatedContentSampleUnavailableReason,
): RelatedContentSampleResult => ({
	status: 'unavailable',
	reason,
});

const reportUnavailable = (
	reason: Exclude<RelatedContentSampleUnavailableReason, 'invalid-input'>,
	cause?: unknown,
) => {
	const causeCategory: RelatedContentSampleCauseCategory =
		cause == null
			? 'none'
			: cause instanceof Error
				? cause.name === 'GraphQLError'
					? 'graphql'
					: 'error'
				: 'non-error';

	ssrPrepareErrorsClient.submitError(
		new Error(`Related content sample unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-related-content-sample',
			reason,
			causeCategory,
		},
	);
};

export const preloadRelatedContentSample = async ({
	cloudId,
	activationId,
	contentId,
	contentType,
}: {
	cloudId: string;
	activationId: string;
	contentId: string;
	contentType?: BylineContentType;
}): Promise<RelatedContentSampleResult> => {
	const variables = buildRelatedContentSampleVariables(
		cloudId,
		activationId,
		contentId,
		contentType,
	);
	if (!variables) {
		return unavailable('invalid-input');
	}

	try {
		// No `no-cache`: the sample warms the AGG query cache so the client can read the same query
		// `cache-only` after SSR hydration.
		const { data, errors, hasErrors } = await queryAgg<
			useRelatedContentQuery$data,
			useRelatedContentQuery$variables
		>({
			query: RelatedContentSampleQuery,
			variables,
		});

		if (hasErrors || errors?.length) {
			reportUnavailable('query-error', errors?.[0]);
			return unavailable('query-error');
		}

		const result = normalizeRelatedContentSampleData(data);
		if (result.status === 'unavailable') {
			reportUnavailable('malformed-response');
		}
		return result;
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return unavailable('query-error');
	}
};
